import { useEffect, useRef, type ReactNode } from 'react';
import { usePiegeFocus } from '../../lib/usePiegeFocus';

// Verrou du défilement partagé par toutes les fenêtres ouvertes. Chacune
// restaurait la valeur lue à son ouverture : deux fenêtres superposées
// fermées d'un même `Échap` (fiche contrat + visionneuse du CERFA) se
// démontent ensemble, la seconde remettait le `hidden` lu sur la première, et
// la page restait sans défilement jusqu'au rechargement — y compris sur
// l'écran suivant. Un compteur ne dépend pas de l'ordre des fermetures.
let verrous = 0;
let debordementInitial = '';

function verrouillerDefilement(): void {
  if (verrous === 0) {
    debordementInitial = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  verrous += 1;
}

function deverrouillerDefilement(): void {
  verrous = Math.max(0, verrous - 1);
  if (verrous === 0) document.body.style.overflow = debordementInitial;
}

/**
 * Fenêtre modale de la plateforme.
 *
 * Elle est pilotable entièrement au clavier, comme l'exige le RGAA
 * (`usePiegeFocus`) : le focus entre dans la fenêtre à l'ouverture — sur la
 * fenêtre elle-même, qui annonce son titre, sans ouvrir le clavier d'un
 * téléphone sur un champ —, `Échap` ferme la fenêtre du dessus, la tabulation
 * reste enfermée dedans, et le focus revient à l'élément qui l'a ouverte à la
 * fermeture. Sans cela, la tabulation continuerait derrière la fenêtre, sur
 * un contenu que la personne ne voit pas.
 */
export function Modale({
  titre,
  sousTitre,
  ouverte,
  onFermer,
  children,
  largeur = 'max-w-2xl',
  zIndice = 'z-50',
  integree = false,
}: {
  titre: string;
  sousTitre?: string;
  ouverte: boolean;
  onFermer: () => void;
  children: ReactNode;
  largeur?: string;
  zIndice?: string;
  /** Rend le contenu dans une page dédiée, sans fond ni piège de focus. */
  integree?: boolean;
}): JSX.Element | null {
  const panneau = useRef<HTMLDivElement>(null);
  const actif = ouverte && !integree;

  usePiegeFocus(panneau, actif, onFermer);

  useEffect(() => {
    if (!actif) return;
    // Le fond ne défile pas derrière la fenêtre : sur mobile, c'est ce qui
    // donne l'impression que le formulaire a disparu.
    verrouillerDefilement();
    return deverrouillerDefilement;
  }, [actif]);

  if (!ouverte) return null;
  if (integree) return <>{children}</>;

  return (
    // Le défilement est porté par ce conteneur, et le centrage par celui qui
    // suit, avec `min-h-full`. Centrer directement ici avec `items-center`
    // rendrait le haut d'un contenu plus grand que la fenêtre inatteignable :
    // le débordement vers le haut d'une boîte flex centrée ne se défile pas.
    <div className={`fixed inset-0 ${zIndice} overflow-y-auto bg-codialis-navy/40`}>
      {/* Le fond ferme la fenêtre, mais n'est pas une cible de tabulation :
          `Échap` et le bouton « Fermer » sont les chemins au clavier. Il reste
          fixe pour couvrir la page pendant le défilement. */}
      <div className="fixed inset-0" onClick={onFermer} aria-hidden="true" />

      <div className="relative flex min-h-full items-stretch justify-center p-2 sm:items-center sm:p-4">
        <div
          ref={panneau}
          role="dialog"
          aria-modal="true"
          aria-label={titre}
          tabIndex={-1}
          className={`relative my-auto w-full ${largeur} rounded-2xl bg-white shadow-card focus:outline-none`}
        >
          {/* En-tête collant : sur un formulaire long, le titre et le bouton
              de fermeture restent visibles pendant le défilement. */}
          <div className="sticky top-0 z-10 flex items-start justify-between gap-3 rounded-t-2xl border-b border-gray-100 bg-white px-4 py-3.5 sm:gap-4 sm:px-6 sm:py-4">
            <div className="min-w-0">
              <h2 className="font-display text-base font-bold text-codialis-navy sm:text-lg">{titre}</h2>
              {sousTitre && <p className="mt-0.5 text-xs text-codialis-gray">{sousTitre}</p>}
            </div>
            <button
              type="button"
              onClick={onFermer}
              aria-label="Fermer"
              className="-mr-1 shrink-0 rounded-lg p-2 text-codialis-gray transition hover:bg-gray-50 hover:text-codialis-navy"
            >
              <svg aria-hidden="true" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
